import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/components/chat/blocks/Chart.tsx
// One chart component. `kind` selects line/bar/area/pie (Recharts). Replaces
// the five separate chart files.
import { memo } from "react";
import { ResponsiveContainer, LineChart as RLine, Line, BarChart as RBar, Bar, AreaChart as RArea, Area, PieChart as RPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, } from "recharts";
const PALETTE = ["#8b5cf6", "#5DCAA5", "#378ADD", "#EF9F27", "#F0997B", "#ED93B1"];
const AXIS = "#94a3b8";
const GRID = "#334155";
const TOOLTIP = { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 };
function toRows(block) {
    const x = block.x ?? [];
    const n = Math.max(x.length, ...block.series.map((s) => s.points.length), 0);
    return Array.from({ length: n }, (_, i) => {
        const row = { x: x[i] ?? String(i) };
        block.series.forEach((s, si) => {
            row[s.name || `series_${si}`] = s.points[i];
        });
        return row;
    });
}
function ChartImpl({ block }) {
    const series = block.series ?? [];
    if (series.length === 0)
        return null;
    const data = toRows(block);
    const names = series.map((s, i) => s.name || `series_${i}`);
    const showLegend = names.length > 1;
    const margin = { top: 8, right: 12, bottom: 8, left: 4 };
    const axes = (_jsxs(_Fragment, { children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: GRID, vertical: false }), _jsx(XAxis, { dataKey: "x", tick: { fill: AXIS, fontSize: 11 }, tickLine: false, axisLine: { stroke: GRID } }), _jsx(YAxis, { tick: { fill: AXIS, fontSize: 11 }, tickLine: false, axisLine: { stroke: GRID } }), _jsx(Tooltip, { contentStyle: TOOLTIP, labelStyle: { color: "#cbd5e1" } }), showLegend && _jsx(Legend, { wrapperStyle: { fontSize: 11, paddingTop: 8 } })] }));
    let chart;
    if (block.kind === "pie") {
        const s = series[0];
        const labels = block.x ?? s.points.map((_, i) => String(i));
        const pieData = labels.map((label, i) => ({ name: label, value: s.points[i] }));
        chart = (_jsxs(RPie, { children: [_jsx(Pie, { data: pieData, dataKey: "value", nameKey: "name", outerRadius: "80%", label: true, children: pieData.map((_, i) => _jsx(Cell, { fill: PALETTE[i % PALETTE.length] }, i)) }), _jsx(Tooltip, { contentStyle: TOOLTIP }), _jsx(Legend, { wrapperStyle: { fontSize: 11 } })] }));
    }
    else if (block.kind === "bar") {
        chart = (_jsxs(RBar, { data: data, margin: margin, children: [axes, names.map((n, i) => _jsx(Bar, { dataKey: n, fill: PALETTE[i % PALETTE.length] }, n))] }));
    }
    else if (block.kind === "area") {
        chart = (_jsxs(RArea, { data: data, margin: margin, children: [axes, names.map((n, i) => (_jsx(Area, { type: "monotone", dataKey: n, stroke: PALETTE[i % PALETTE.length], fill: PALETTE[i % PALETTE.length], fillOpacity: 0.2 }, n)))] }));
    }
    else {
        chart = (_jsxs(RLine, { data: data, margin: margin, children: [axes, names.map((n, i) => (_jsx(Line, { type: "monotone", dataKey: n, stroke: PALETTE[i % PALETTE.length], strokeWidth: 2, dot: false, connectNulls: true }, n)))] }));
    }
    return (_jsxs("div", { children: [block.title && (_jsx("div", { className: "text-[11px] uppercase tracking-wider text-slate-400 mb-1.5", children: block.title })), _jsx("div", { className: "rounded-xl border border-slate-700/60 bg-slate-900/30 p-2", children: _jsx(ResponsiveContainer, { width: "100%", height: 280, children: chart }) })] }));
}
export const Chart = memo(ChartImpl);
